import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, UseFormSetValue, UseFormWatch, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { DataTable, type DataTableColumnDef } from '@column/ui';

import { Asterix } from '../_components/Asterix';
import { AuthorizationCell } from '../_components/AuthorizationCell';
import { CounterCell } from '../_components/CounterCell';
import { ReviewFormValues, TransferState, TransferTemplateForm } from '../types';
import { BankAccountCell, CounterpartyCell, FormCell, StateCell, StateCellOption } from '~/components/Table';
import { useCreateWireTransfer } from '~/hooks/useTransfers';
import { useUpdateTransferTemplate } from '~/hooks/useTransferTemplates';
import { Action } from '~/lib/authz';
import { WireTransferTemplateResponse } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { buildWireTransferUpdateRequest } from './api';
import { getColumnVisibility } from './shared';

export const defaultWireColumns = ['amount', 'counterpartyId', 'bankAccountId', 'description'];

export const isWireTransferAuthorized = (
  row: WireTransferTemplateResponse,
  isAuthorized: (action: Action) => boolean
) => {
  return row.bankAccountId
    ? isAuthorized({
        permission: 'wireTransfers',
        permissionLevel: 'write',
        bankAccount: { id: row.bankAccountId },
      })
    : true;
};

export const buildWireEditColumns = (
  setValue: UseFormSetValue<TransferTemplateForm>
): DataTableColumnDef<WireTransferTemplateResponse & { isAuthorized?: boolean }>[] => [
  {
    accessor: 'isAuthorized',
    header: '',
    cell: ({ value }) => <AuthorizationCell isAuthorized={value} />,
    width: '32px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`wireTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable
            currency="USD"
            value={value}
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'counterpartyId',
    header: (
      <>
        Counterparty
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Counterparty',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`wireTransfers.${rowIndex}.counterpartyId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <CounterpartyCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`wireTransfers.${rowIndex}.counterpartyDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
    sticky: 'left',
  },
  {
    accessor: 'bankAccountId',
    header: (
      <>
        Bank Account
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Bank Account',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`wireTransfers.${rowIndex}.bankAccountId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <BankAccountCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`wireTransfers.${rowIndex}.bankAccountDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`wireTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell isEditable value={value} placeholder="Description" onValueChange={onChange} />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 2fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`wireTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable
            placeholder="Please select"
            value={String(typeof value !== 'boolean' ? true : value)}
            onValueChange={(newValue) => onChange(newValue === 'true')}
            options={[
              { label: 'Allow', value: 'true' },
              { label: 'Deny', value: 'false' },
            ]}
          />
        )}
      </FormCell>
    ),
    width: '148px',
  },
];

export const wireReviewColumns: DataTableColumnDef<
  Partial<WireTransferTemplateResponse> & { id: string; state: TransferState }
>[] = [
  {
    accessor: 'id',
    header: '',
    cell: ({ rowIndex }) => <CounterCell value={rowIndex + 1} />,
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'state',
    header: '',
    cell: ({ value }) => (
      <StateCell state={value.type as StateCellOption} message={value.type === 'error' ? value.message : undefined} />
    ),
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`wireTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable={row.state.type === 'error'}
            value={value as number | undefined}
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
            currency="USD"
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'counterpartyId',
    header: 'Counterparty',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.counterpartyDescription ? (
        <DataTable.TextCell value={row.counterpartyDescription} />
      ) : (
        <FormCell id={`wireTransfers.${rowIndex}.counterpartyId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <CounterpartyCell
              isEditable={row.state.type === 'error'}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v.id)}
            />
          )}
        </FormCell>
      ),
    width: 'minmax(200px, 1fr)',
    sticky: 'left',
  },
  {
    accessor: 'bankAccountId',
    header: 'Bank Account',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.bankAccountDescription ? (
        <DataTable.TextCell value={row.bankAccountDescription} />
      ) : (
        <FormCell id={`wireTransfers.${rowIndex}.bankAccountId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <BankAccountCell
              isEditable={row.state.type === 'error'}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v.id)}
            />
          )}
        </FormCell>
      ),
    width: 'minmax(200px, 1fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`wireTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Description"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 1fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`wireTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable={row.state.type === 'error'}
            value={String((value as boolean) ?? false)}
            onValueChange={(v) => onChange(v === 'true')}
            placeholder="Please select"
            options={['Allow', 'Deny'].map((code: string) => ({
              label: code,
              value: String(code === 'Allow'),
            }))}
          />
        )}
      </FormCell>
    ),
    width: '144px',
  },
];

export const useWireTransfersEditTable = () => {
  const { isAuthorized } = useSessionStore();
  const { control, setValue, watch, resetField } = useFormContext<TransferTemplateForm>();
  const wireTransfers = watch('wireTransfers');
  const { append, remove } = useFieldArray({
    control,
    name: 'wireTransfers',
  });
  const transfersWithPermission = useMemo(
    () =>
      (wireTransfers ?? []).map((transfer) => ({
        ...transfer,
        isAuthorized: isWireTransferAuthorized(transfer, isAuthorized),
      })),
    [JSON.stringify(wireTransfers), isAuthorized]
  );
  const allTransfersAuthorized = transfersWithPermission.every((transfer) => transfer.isAuthorized);
  const wireColumns = useMemo(() => buildWireEditColumns(setValue), [setValue]);

  const visibleColumns = watch('visibleColumns');
  const wireColumnVisibility = useMemo(
    () => ({
      ...getColumnVisibility(wireColumns, visibleColumns),
      isAuthorized: !allTransfersAuthorized,
    }),
    [wireColumns, allTransfersAuthorized, visibleColumns]
  );

  const wireAppend = useCallback(() => {
    append({
      transferTemplateRowId: uuid(),
      amount: undefined,
      counterpartyId: undefined,
      bankAccountId: watch('wireDefaultParameters.bankAccountId'),
      description: watch('wireDefaultParameters.description'),
      allowOverdraft: true,
    });
  }, [append, watch]);

  const wireRemove = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }
      const index = wireTransfers?.findIndex((transfer) => transfer.transferTemplateRowId === id) ?? -1;
      if (index === -1) {
        return;
      }
      remove(index);
      resetField('wireTransfers', {
        defaultValue: (wireTransfers ?? []).filter((_, i) => i !== index),
        keepDirty: true,
        keepError: true,
        keepTouched: true,
      });
    },
    [remove, resetField, wireTransfers]
  );

  return {
    append: wireAppend,
    remove: wireRemove,
    data: transfersWithPermission,
    columns: wireColumns,
    columnVisibility: wireColumnVisibility,
  };
};

export const useWireTransfersReviewTable = ({
  control,
  watch,
  transferTemplateId,
}: {
  control: Control<ReviewFormValues>;
  watch: UseFormWatch<ReviewFormValues>;
  transferTemplateId: string;
}) => {
  const { fields: transfers, update } = useFieldArray({ control, name: 'wireTransfers' });
  const [states, setStates] = useState<TransferState[]>([]);
  const { addDangerNotification } = useNotificationStore();
  const { createRequest: createWireTransfer } = useCreateWireTransfer();
  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate();

  useEffect(() => {
    setStates((prev) =>
      prev.length === transfers.length ? prev : Array.from({ length: transfers.length }, () => ({ type: 'idle' }))
    );
  }, [transfers.length]);

  const setState = useCallback((index: number, state: TransferState) => {
    setStates((prev) => prev.map((s, i) => (i === index ? state : s)));
  }, []);

  const handleRow = useCallback(
    async (index: number): Promise<boolean> => {
      const currentState = states[index] || { type: 'idle' };
      if (currentState.type === 'success') return false;

      setState(index, { type: 'loading' });
      try {
        const wireData = watch(`wireTransfers.${index}`) as WireTransferTemplateResponse;
        const idempotencyKeyValue = uuid();

        await createWireTransfer({
          amount: `${wireData.amount}`,
          counterpartyId: wireData.counterpartyId!,
          bankAccountId: wireData.bankAccountId,
          description: wireData.description,
          allowOverdraft: wireData.allowOverdraft,
          currencyCode: 'USD',
          idempotencyKey: idempotencyKeyValue,
        });

        setState(index, { type: 'success' });
        return true;
      } catch (e: any) {
        const errorMessage = e?.message ? `${e.message} ${e?.details?.reason ?? ''}` : 'Unknown error occurred';
        setState(index, { type: 'error', message: errorMessage });
        addDangerNotification({
          content: (
            <>
              <span className="underline">{index + 1}. Transfer</span> {errorMessage}
            </>
          ),
          timeout: 8000,
        });
        return false;
      }
    },
    [states, setState, watch, createWireTransfer, update, addDangerNotification]
  );

  const applyErrorCorrectionsAndUpdateTemplate = useCallback(async () => {
    const curData = watch('wireTransfers');
    const updateRequest = buildWireTransferUpdateRequest(curData);
    const updatedTemplate = await updateTransferTemplate({
      transferTemplateId,
      wireTransfers: updateRequest,
    });
    if (!updatedTemplate) return [] as WireTransferTemplateResponse[];

    const updatedWireTransfers = updatedTemplate.wireTransfers || [];
    updatedWireTransfers.forEach((updatedTransfer: WireTransferTemplateResponse, i: number) => {
      const originalTransfer = curData[i];
      update(i, {
        ...originalTransfer,
        bankAccountDescription: updatedTransfer.bankAccountDescription,
        counterpartyDescription: updatedTransfer.counterpartyDescription,
      });
    });
    return updatedWireTransfers as WireTransferTemplateResponse[];
  }, [watch, transferTemplateId, updateTransferTemplate, update]);

  const visibleColumns = useWatch({ control, name: 'visibleColumns' });
  const wireColumnVisibility = useMemo(
    () => ({ ...getColumnVisibility(wireReviewColumns, visibleColumns), id: true, state: true }),
    [visibleColumns]
  );

  const data = useMemo(
    () =>
      transfers.map((transfer, i) => ({
        ...transfer,
        id: transfer.id ?? String(i),
        state: states[i] ?? ({ type: 'idle' } as TransferState),
      })),
    [transfers, states]
  );

  return {
    columns: wireReviewColumns,
    columnVisibility: wireColumnVisibility,
    data,
    states,
    handleRow,
    applyErrorCorrectionsAndUpdateTemplate,
  };
};
